import { ConsentStatus, ConsentType } from '@capacitor-firebase/analytics/dist/esm/definitions';
import { Inject, Injectable } from '@angular/core';

import { Analytics, logEvent, setAnalyticsCollectionEnabled, setConsent } from 'firebase/analytics';

import { FIREBASE_ANALYTICS_IT } from '../../../../Application/Provider/injection-token/firebase-origin-service.it';
import { AnalyticsEvent } from '../../../../Domain/Model/analytics.event';
import { EventRepository } from '../../../../Domain/Port/event-repository';
import { FirebaseErrorLogger } from '../../../../Domain/Port/firebase-error.logger';

/**
 * A repository for handling web events and logging them to Firebase Analytics.
 * Extends the base EventRepository class.
 *
 * This class provides functionality to log analytics events using Firebase Analytics
 * and ensures errors during event logging are properly handled and passed to the
 * FirebaseErrorLogger for further processing.
 *
 * This service is injectable and designed to be used throughout the application.
 */
@Injectable({
    providedIn: 'root'
})
export class FirebaseWebEventRepository extends EventRepository {
    private readonly consentEvents: ConsentType[] = [
        ConsentType.AnalyticsStorage,
        ConsentType.FunctionalityStorage,
        ConsentType.AdUserData,
        ConsentType.AdPersonalization,
        ConsentType.PersonalizationStorage,
        ConsentType.AdStorage
    ];
    private readonly setConsent = setConsent;
    private readonly analyticsLogEvent = logEvent;
    private readonly setAnalyticsCollectionEnabled = setAnalyticsCollectionEnabled;

    constructor(
        @Inject(FIREBASE_ANALYTICS_IT) private readonly analytics: Analytics,
        private readonly firebaseErrorLogger: FirebaseErrorLogger
    ) {
        super();
    }

    /**
     * Sets the analytics consent status for all consent types.
     *
     * This method enables or disables an analytics collection based on the provided consent status
     * and sets the appropriate consent status for all consent types defined in consentEvents.
     *
     * @param isGiven - A boolean indicating whether consent is given (true) or denied (false)
     * @returns A Promise that resolves when the consent status has been set
     */
    override setAnalyticsConsent(isGiven: boolean): Promise<void> {
        this.setAnalyticsCollectionEnabled(this.analytics, true);

        const status: ConsentStatus = isGiven ? ConsentStatus.Granted : ConsentStatus.Denied;
        this.consentEvents.forEach(consentType => this.setConsent({ type: consentType, status }));
        return Promise.resolve();
    }

    /**
     * Logs an analytics event to Firebase Analytics.
     *
     * This method attempts to log the provided event to Firebase Analytics.
     * If an error occurs during the logging process, it is caught and passed
     * to the FirebaseErrorLogger for proper error handling.
     *
     * @param event - The analytics event to log, containing a name and optional parameters
     * @returns A Promise that resolves when the event has been logged or the error has been handled
     */
    async logEvent(event: AnalyticsEvent): Promise<void> {
        try {
            console.log(`Logging event: ${event.name} with params: ${JSON.stringify(event.params)}`);
            this.analyticsLogEvent(this.analytics, event.name, event.params);
        } catch (error) {
            await this.firebaseErrorLogger.logError(event, error);
        }
    }
}
